import { fiveMinutes } from '@data/constants/time'
import { defaultOnError } from '@data/queries/defaultOnError'
import { hasLimitedMemory } from '@internations/ds'
import { QueryCache, QueryClientConfig } from '@tanstack/react-query'
import { hasLimitedBandwidth, isAndroidContainerApp } from '@util/device'
import { isAxiosError } from 'axios'

const LOW_BANDWIDTH_STALETIME = fiveMinutes
const HIGH_BANDWIDTH_STALETIME = 2 * 60 * 1000
const isLowBandwidthClient = hasLimitedBandwidth() || isAndroidContainerApp()
const staleTime = isLowBandwidthClient
    ? // Limited-bandwidth devices use a longer `staleTime` to lessen the amount of network requests
      LOW_BANDWIDTH_STALETIME
    : HIGH_BANDWIDTH_STALETIME
const LOW_MEMORY_DEVICE_GCTIME = 1 * 60 * 1000
const HIGH_MEMORY_DEVICE_GCTIME = 2 * 60 * 1000
const isLowMemoryDevice = hasLimitedMemory() || isAndroidContainerApp()
const gcTimeBasedOnMemory = isLowMemoryDevice ? LOW_MEMORY_DEVICE_GCTIME : HIGH_MEMORY_DEVICE_GCTIME
const gcTime =
    process.env.STAGE === 'test'
        ? // https://tanstack.com/query/latest/docs/framework/react/guides/testing#set-gctime-to-infinity-with-jest
          Infinity
        : // Lower-performing devices use a shorter `gcTime` to allow them to free up memory more readily
          gcTimeBasedOnMemory

function retryQuery(failureCount: number, error: unknown) {
    // Don't retry if we get a 4xx error
    if (isAxiosError(error) && error.response?.status != null && error.response.status < 500) {
        return false
    }

    return failureCount < 4
}

// https://tanstack.com/query/latest/docs/framework/react/guides/testing#turn-off-retries
const retry = process.env.STAGE === 'test' ? false : retryQuery

const defaultOptions: QueryClientConfig['defaultOptions'] = {
    queries: {
        staleTime,
        gcTime,
        retry,
    },
}

/**
 * Returns a query client config with our preferred options and a new query cache
 */
export function createQueryClientConfig(): QueryClientConfig {
    return {
        defaultOptions,
        queryCache: new QueryCache({
            onError: async (error, query) => {
                await defaultOnError(error, query)
            },
        }),
    }
}
